import {
  ColumnDef,
  createColumnHelper,
  FilterFnOption,
} from '@tanstack/react-table';
import { useCallback, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton } from 'src/components/Buttons';
import { MarketplaceSquareLogo } from 'src/components/common/MarketplaceSquareLogo';
import { getSeatDisplayText } from 'src/components/common/SeatingInfo';
import { EventDateDisplay } from 'src/components/Events/EventInfo';
import { ListingNotesDisplay } from 'src/components/Listings/ListingDisplay/ListingNotesDisplay';
import { CommissionsCell } from 'src/components/Purchases/CommissionsCell';
import { BaseCell, SvgCell, TextCell } from 'src/components/TableV2/cells';
import { ColumnPicker } from 'src/components/TableV2/components/ColumnPicker';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_XL,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { HorizontalTextList } from 'src/core/POS/HorizontalTextList';
import * as PosCollapsible from 'src/core/POS/PosCollapsible';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useConsigneeAccountDisplayName } from 'src/hooks/api/useConsigneeAccountDisplayName';
import { useOffices } from 'src/hooks/api/useOffices';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { useMapDeliveryTypesToString } from 'src/hooks/useMapDeliveryTypesToString';
import { FormatOption } from 'src/modals/EditTableColumns';
import { LaunchDownloadUserDocuments } from 'src/modals/PurchaseWizard/LaunchDownloadUserDocuments';
import { PredeliveryActionsDropdown } from 'src/modals/PurchaseWizard/PurchaseTicketsFlow/components/PredeliveryActionsDropdown';
import { UploadPredeliveryAction } from 'src/modals/PurchaseWizard/PurchaseTicketsFlow/purchaseTicketsFlow.utils';
import { TicketGroupEditContext } from 'src/modals/PurchaseWizard/PurchaseWizard.types';
import { MainRoute } from 'src/navigations/Routes/MainRoute';
import { OpenLinkIcon } from 'src/svgs';
import {
  EditIcon,
  ExpandIcon,
  ForwardIcon,
  IconsFill,
  PaymentSize,
  UploadIcon,
} from 'src/svgs/Viagogo';
import { SEATING_SORTING_FN_NAME } from 'src/tables/common/hooks/useSeatingColumnSorting';
import { TableShimmeringDiv } from 'src/tables/Table';
import { createDateColumn } from 'src/tables/TableV2/factories/dateColumnFactory';
import { createIconTagColumn } from 'src/tables/TableV2/factories/iconTagColumnFactory';
import { createLinkColumn } from 'src/tables/TableV2/factories/linkColumnFactory';
import { createMoneyColumn } from 'src/tables/TableV2/factories/moneyColumnFactory';
import { createNumberColumn } from 'src/tables/TableV2/factories/numberColumnFactory';
import { createSeatingColumn } from 'src/tables/TableV2/factories/seatingColumnFactory';
import {
  createStatusColumn,
  PURCHASE_PAYMENT_STATUS_MAPPING,
} from 'src/tables/TableV2/factories/statusColumnFactory';
import { createUserTagColumn } from 'src/tables/TableV2/factories/userTagColumnFactory';
import { useApiFetchedUser } from 'src/tables/TableV2/factories/userTagColumnHooks';
import {
  multiSelectFilter,
  seatRangeFilter,
  seatTraitsFilter,
} from 'src/tables/TableV2/filters/filterFunctions';
import { PurchasesTableColumnId } from 'src/utils/columns/purchases/purchasesColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import {
  PURCHASE_PAYMENT_STATUS_TO_CID,
  PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID,
  QUALITY_CONTROL_STATE_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import {
  EVENT_TYPE_PARKING,
  getEventPerformerVenue,
  lookupEventInCatalog,
} from 'src/utils/eventWithDataUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { getPaymentMethodIcon } from 'src/utils/paymentMethodUtils';
import { posField } from 'src/utils/posFieldUtils';
import {
  combineWithTicketGroupId,
  formatConversionRate,
  getPurchaseOrderDeepLinkUrl,
  getPurchaseOrderDetailsModalConfigWithDeepLink,
  getPurchaseOrderRelativeUrl,
  isCancelledState,
} from 'src/utils/purchaseUtils';
import {
  sortGeneric,
  sortMoney,
  sortSeatingSeatsForTable,
} from 'src/utils/tableUtils';
import { getDeliveryTypeIcon } from 'src/utils/ticketTypeUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  Commission,
  DeliveryType,
  Event,
  ListingNote,
  Marketplace,
  PurchaseOrderDetails,
  PurchaseOrderTicketGroup,
  PurchasePaymentMethod,
  QualityControlState,
  Seating,
  TicketGroupEditReason,
  UiMoney,
} from 'src/WebApiController';

import { SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE } from '../PurchaseTable.types';
import { PurchaseActionsCell } from './PurchaseActionsCell';

/**
 * Type guard to check if a string is a valid Marketplace enum value
 */
export function isMarketplace(value: string): value is Marketplace {
  if (value === Marketplace.Offline) {
    return false;
  }

  return Object.values(Marketplace).includes(value as Marketplace);
}

import { createCopyColumn } from 'src/tables/TableV2/factories/copyColumnFactory';

import * as styles from './PurchaseTableColumnsConfig.css';

const columnHelper = createColumnHelper<PurchaseOrderTicketGroup | null>();

const getSeating = (
  purchaseOrderTicketGroup: PurchaseOrderTicketGroup | null
): Seating | null => {
  if (!purchaseOrderTicketGroup) {
    return null;
  }
  const ticketGroupToGetSeating = purchaseOrderTicketGroup.isTktGrp
    ? purchaseOrderTicketGroup
    : purchaseOrderTicketGroup.ticketGroupItems?.[0];

  if (ticketGroupToGetSeating?.seating == null) {
    return null;
  }

  return ticketGroupToGetSeating.seating;
};

const SECTION_TYPE = SectionType.Purchases;

export const getPurchaseTableColumnsConfigV2 = (
  isSearchV2 = false
): ColumnDef<
  PurchaseOrderTicketGroup | null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
>[] => [
  // AccordianToggle Column
  columnHelper.accessor((data) => (data ? data : null), {
    id: PurchasesTableColumnId.AccordianToggle,
    enableSorting: false,
    header: '',
    enableColumnFilter: false,
    size: 16,
    minSize: 16,
    maxSize: 16,
    meta: {
      styleOverrides: { padding: 0, borderRight: 'none' },
    },
    cell: ({
      row: { original, toggleExpanded, getCanExpand, getIsExpanded },
    }) => {
      const canExpand = getCanExpand();
      const isExpanded = getIsExpanded();
      const Icon = isExpanded ? ExpandIcon : ForwardIcon;

      if (original?.isTktGrp) {
        return null;
      }

      return canExpand ? (
        <Icon
          size={vars.iconSize.s}
          withHoverEffect
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleExpanded(!isExpanded);
          }}
        />
      ) : null;
    },
  }),
  // PurchaseDate Column
  createDateColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.PurchaseDate,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.PurchaseDate
      ],
    accessor: (data) => (data ? data.poDate || '' : null),
    size: COLUMN_DEFAULT_SIZE_XL,
    enableColumnFilter: true,
    sectionType: SectionType.Purchases,
    sortingFn: 'datetime',
    customCell: function PurchaseDateCell({
      getValue,
      row: { original },
      table,
      column,
    }) {
      const columnId = column.id;
      const value = getValue();
      const rowSupportsAccordion = table.options.meta?.rowSupportsAccordion;
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType || SectionType.Purchases
      );
      const { timeZone } = useSiteTimezoneContext();

      const date = applyNumberFormatting({
        precision: userDefinedPrecision,
        inputNumber: value,
        isDateTime: true,
        enableUtcFallback: true,
        timeZone,
      }) as string;

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }
      // We don't want to display this if the row is a ticket group
      if (
        original.isTktGrp &&
        table.options.meta?.sectionType !== SectionType.PurchaseEvent &&
        table.options.meta?.sectionType !== SectionType.PurchaseSideTable
      ) {
        return null;
      }

      return (
        <BaseCell>
          <Stack direction="row" gap="m" alignItems="center">
            {rowSupportsAccordion && (
              <PosCollapsible.Trigger
                onClick={(e) => e.stopPropagation()}
                className={styles.purchaseOrderTableCollapseTrigger}
                FoldIcon={ExpandIcon}
                ExpandIcon={ForwardIcon}
                iconSize={vars.iconSize.s}
              />
            )}
            {value == null ? <Content id={ContentId.NA} /> : <div>{date}</div>}
          </Stack>
        </BaseCell>
      );
    },
  }),
  createLinkColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.VendorOrderId,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.VendorOrderId
      ],
    linkAccessor: (data) =>
      data?.id ? getPurchaseOrderRelativeUrl(data.id) : null,
    textAccessor: (data) => (data?.vendOrdId ? String(data.vendOrdId) : ''),
    size: COLUMN_DEFAULT_SIZE_XL,
    shouldRenderCell: (data, table) => {
      // We don't want to display this if the row is a ticket group
      const sectionType = table.options.meta?.sectionType;
      if (
        data?.isTktGrp &&
        sectionType !== SectionType.PurchaseEvent &&
        sectionType !== SectionType.PurchaseSideTable
      ) {
        return false;
      }
      return true;
    },
    rightComponentRender: (data, table) => {
      if (!data) return null;
      const isCancelled = isCancelledState(data.poState);
      return isCancelled ? (
        <div className={styles.purchaseOrderLabel} title={data.poState}>
          {data.poState}
        </div>
      ) : null;
    },
  }),
  // Id Column
  createLinkColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.Id,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[PurchasesTableColumnId.Id],
    linkAccessor: (data) =>
      data?.id ? getPurchaseOrderRelativeUrl(data.id) : null,
    textAccessor: (data) => (data?.id ? String(data.id) : ''),
    size: COLUMN_DEFAULT_SIZE_XL,
    enableSorting: isSearchV2 ? false : undefined,
    shouldRenderCell: (data, table) => {
      // We don't want to display this if the row is a ticket group
      const sectionType = table.options.meta?.sectionType;
      if (
        data?.isTktGrp &&
        sectionType !== SectionType.PurchaseEvent &&
        sectionType !== SectionType.PurchaseSideTable
      ) {
        return false;
      }
      return true;
    },
  }),
  createIconTagColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.Vendor,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[PurchasesTableColumnId.Vendor],
    accessor: (data) => (data ? data.vendor?.name || null : null),
    iconMapper: (vendorName) => {
      if (!vendorName || !isMarketplace(vendorName)) {
        return null;
      }

      return <MarketplaceSquareLogo marketplace={vendorName} size={20} />;
    },
    showText: true,
    size: COLUMN_DEFAULT_SIZE_BASE,
    enableColumnFilter: true,
  }),
  // Shipment Column
  columnHelper.accessor((data) => (data ? data.delivType : null), {
    id: PurchasesTableColumnId.Shipment,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.Shipment
          ]
        }
      />
    ),
    enableColumnFilter: true,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, row: { original } }) {
      const delivType = getValue<DeliveryType>();
      const strDelivType = useMapDeliveryTypesToString({
        deliveryTypesObj: {
          delivType,
        },
      });

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }
      if (!delivType) return null;
      const icon = getDeliveryTypeIcon(delivType, null, IconsFill.textStrong);
      return (
        <BaseCell align="center" title={strDelivType}>
          <SvgCell
            value={icon}
            svgWrapperStyle={{
              padding: '4px',
              backgroundColor: vars.color.surfaceSecondary,
              borderRadius: vars.borderRadius.s,
            }}
          />
        </BaseCell>
      );
    },
    filterFn: multiSelectFilter,
    meta: {
      columnFilterVariant: 'multiSelect',
    },
  }),
  // Orders Column
  createNumberColumn({
    id: PurchasesTableColumnId.Orders,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[PurchasesTableColumnId.Orders],
    accessor: (data) => (data ? data.tktGrpCnt : null),
    size: COLUMN_DEFAULT_SIZE_BASE,
    withFooter: true,
    useUserPrecision: false,
    shouldRender: (data, table) => {
      if (
        data?.isTktGrp &&
        table.options.meta?.sectionType !== SectionType.PurchaseEvent &&
        table.options.meta?.sectionType !== SectionType.PurchaseSideTable
      ) {
        return false;
      }
      return true;
    },
  }),
  // Tickets Column
  createNumberColumn({
    id: PurchasesTableColumnId.Tickets,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[PurchasesTableColumnId.Tickets],
    accessor: (data) => (data ? data.ticketCnt : null),
    size: COLUMN_DEFAULT_SIZE_BASE,
    useUserPrecision: false,
    withFooter: true,
  }),
  // Payment Column
  columnHelper.accessor((data) => (data ? data.pmtMethod : null), {
    id: PurchasesTableColumnId.Payment,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.Payment
          ]
        }
      />
    ),
    enableColumnFilter: true,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, row: { original }, table }) {
      const value = getValue();
      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      // We don't want to display this if the row is a ticket group
      if (
        original.isTktGrp &&
        table.options.meta?.sectionType !== SectionType.PurchaseEvent &&
        table.options.meta?.sectionType !== SectionType.PurchaseSideTable
      ) {
        return null;
      }
      if (!value) return <BaseCell />;

      const pmtMethods = original.pmtMtdFullDisp;
      const PurchaseMethodIcon = getPaymentMethodIcon(value);

      if (!pmtMethods || pmtMethods.length === 0) {
        return <BaseCell />;
      }

      const paymentMethods = pmtMethods.map(
        (method: PurchasePaymentMethod) => `${method?.display} ${method?.name}`
      );
      return (
        <BaseCell align="right" title={paymentMethods.join(', ')}>
          <HorizontalTextList
            preIcon={
              <PurchaseMethodIcon size={PaymentSize.s} isSquare={false} />
            }
            texts={paymentMethods}
            maxTextsToShowOnHover={paymentMethods.join(', ').length}
          />
        </BaseCell>
      );
    },
    filterFn: multiSelectFilter,
    meta: {
      columnFilterVariant: 'multiSelect',
    },
  }),
  // Cost Column
  createMoneyColumn({
    id: PurchasesTableColumnId.Cost,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[PurchasesTableColumnId.Cost],
    accessor: (data) => (data ? data.totalCst : null),
    size: COLUMN_DEFAULT_SIZE_LG,
    withFooter: true,
  }),

  // TotalCost Column
  createMoneyColumn({
    id: PurchasesTableColumnId.TotalCost,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.TotalCost
      ],
    accessor: (data) => (data ? data.totalCst : null),
    size: COLUMN_DEFAULT_SIZE_LG,
    withFooter: true,
  }),

  // UnitCost Column
  createMoneyColumn({
    id: PurchasesTableColumnId.UnitCost,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.UnitCost
      ],
    accessor: (data) => (data ? data.unitCst : null),
    size: COLUMN_DEFAULT_SIZE_LG,
    withFooter: true,
  }),

  // FaceValueCost Column (Search V1 only)
  ...(!isSearchV2
    ? [
        createMoneyColumn({
          id: PurchasesTableColumnId.FaceValueCost,
          headerContentId:
            PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
              PurchasesTableColumnId.FaceValueCost
            ],
          accessor: (data) => (data ? data?.faceValCst : null),
          size: COLUMN_DEFAULT_SIZE_LG,
        }) as ColumnDef<PurchaseOrderTicketGroup | null>,
      ]
    : []),

  // TotalPayment Column
  createMoneyColumn({
    id: PurchasesTableColumnId.TotalPayment,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.TotalPayment
      ],
    accessor: (data) => (data ? data.totalPmt : null),
    size: COLUMN_DEFAULT_SIZE_LG,
    withFooter: true,
    enableColumnFilter: false,
  }),
  // TotalConvertedPayment Column
  createMoneyColumn({
    id: PurchasesTableColumnId.TotalConvertedPayment,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.TotalConvertedPayment
      ],
    accessor: (data) => (data ? data.totalConvertedPmt : null),
    size: COLUMN_DEFAULT_SIZE_LG,
    withFooter: true,
    enableColumnFilter: false,
  }),
  // OutstandingBalance Column
  createMoneyColumn({
    id: PurchasesTableColumnId.OutstandingBalance,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.OutstandingBalance
      ],
    accessor: (data) => (data ? data.outsBal : null),
    size: COLUMN_DEFAULT_SIZE_LG,
    withFooter: true,
  }),
  // PaymentConversionRate Column
  columnHelper.accessor((data) => (data ? data.pmtConvRate : null), {
    id: PurchasesTableColumnId.PaymentConversionRate,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.PaymentConversionRate
          ]
        }
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: ({ getValue, row: { original } }) => {
      const value = getValue();
      if (original == null) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }
      return (
        <BaseCell align="right">
          {formatConversionRate(
            value,
            original.totalPmt?.currency,
            original.totalConvertedPmt?.currency
          )}
        </BaseCell>
      );
    },
    sortingFn: sortMoney<PurchaseOrderTicketGroup>,
  }),
  // PaymentStatus Column
  createStatusColumn({
    id: PurchasesTableColumnId.PaymentStatus,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.PaymentStatus
      ],
    accessor: (data) => (data ? data.pmtStat : null),
    size: COLUMN_DEFAULT_SIZE_LG,
    shouldRender: (data, table) => {
      const sectionType = table.options.meta?.sectionType;
      if (
        data?.isTktGrp &&
        sectionType !== SectionType.PurchaseEvent &&
        sectionType !== SectionType.PurchaseSideTable
      ) {
        return false;
      }
      return true;
    },
    statusMapping: PURCHASE_PAYMENT_STATUS_MAPPING,
    contentMapping: PURCHASE_PAYMENT_STATUS_TO_CID,
  }),
  // QualityControlState Column
  columnHelper.accessor((data) => (data ? (data.qcState ?? null) : undefined), {
    id: PurchasesTableColumnId.QualityControlState,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.QualityControlState
          ]
        }
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: ({ getValue, row: { original }, table }) => {
      // We don't want to display this if the row is a ticket group
      if (
        original?.isTktGrp &&
        table.options.meta?.sectionType !== SectionType.PurchaseEvent &&
        table.options.meta?.sectionType !== SectionType.PurchaseSideTable
      ) {
        return null;
      }

      const value = getValue() as QualityControlState;
      if (value === undefined) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }
      return (
        <BaseCell>
          <Content
            id={
              value == null
                ? ContentId.Unset
                : QUALITY_CONTROL_STATE_TO_CID[value]
            }
          />
        </BaseCell>
      );
    },
  }),
  // BuyerUserId Column
  createUserTagColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.BuyerUserId,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.BuyerUserId
      ],
    accessor: (data) => data?.buyerId,
    size: COLUMN_DEFAULT_SIZE_LG,
  }),
  // Actions Column
  columnHelper.display({
    id: PurchasesTableColumnId.Actions,
    enableSorting: false,
    enableResizing: true,
    header: function Cell() {
      const { mainRoute } = useAppContext();
      const isEventPage = isInFullPageEventView(mainRoute);
      return isEventPage ? (
        <ColumnPicker tableColumnsSectionType={SectionType.Purchases} />
      ) : null;
    },
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ row: { original }, table }) {
      const { setActivePosEntity } =
        useActivePosEntityContext<PurchaseOrderDetails>();
      const { setModal } = useContext(ModalContext);
      const { mainRoute } = useAppContext();
      const navigate = useNavigate();

      const onAmendTickets = useCallback(
        (ticketGroupEditContext?: Partial<TicketGroupEditContext>) => {
          const ticketGroupId = original?.isTktGrp
            ? original!.tgId!
            : original?.ticketGroupItems?.[0]?.tgId;
          setActivePosEntity(original!.id, undefined, true);
          ticketGroupId &&
            setModal(
              getPurchaseOrderDetailsModalConfigWithDeepLink(
                combineWithTicketGroupId(original!.id, {
                  ...ticketGroupEditContext,
                  ticketGroupId,
                  editIntention: TicketGroupEditReason.AmendTickets,
                })
              )
            );
        },
        [original, setActivePosEntity, setModal]
      );

      const ticketGroupNavId = useMemo(() => {
        const ticketGroupId = original?.isTktGrp
          ? original!.tgId!
          : original?.ticketGroupItems?.[0]?.tgId;

        return (
          original &&
          ticketGroupId &&
          combineWithTicketGroupId(original.id, {
            ticketGroupId: ticketGroupId,
          })
        );
      }, [original]);

      const onViewTicketGroup = useCallback(
        (newTab: boolean) => {
          const inFullPageEventView = isInFullPageEventView(mainRoute);
          if (ticketGroupNavId) {
            if (newTab) {
              window.open(
                getPurchaseOrderDeepLinkUrl(ticketGroupNavId),
                '_blank'
              );
            } else if (
              mainRoute === MainRoute.Purchases ||
              mainRoute === MainRoute.PurchaseSearch ||
              mainRoute === MainRoute.PurchaseSearchV2 ||
              inFullPageEventView
            ) {
              setActivePosEntity(original!.id, undefined, true);
              setModal(
                getPurchaseOrderDetailsModalConfigWithDeepLink(ticketGroupNavId)
              );
            } else {
              navigate(getPurchaseOrderRelativeUrl(ticketGroupNavId));
            }
          } else {
            if (newTab) {
              window.open(getPurchaseOrderDeepLinkUrl(original!.id), '_blank');
            } else {
              setActivePosEntity(original!.id, undefined, true);
              setModal(
                getPurchaseOrderDetailsModalConfigWithDeepLink(original!.id)
              );
            }
          }
        },
        [
          mainRoute,
          navigate,
          original,
          setActivePosEntity,
          setModal,
          ticketGroupNavId,
        ]
      );

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      const linkOnly =
        table.options.meta?.formatOptionOverrides?.[
          PurchasesTableColumnId.Actions
        ] === FormatOption.Actions_LinkOnly;

      let predeliveryActions = null;
      const ticketGroup = original.isTktGrp
        ? original
        : original.ticketGroupItems?.[0];

      if (!linkOnly && ticketGroup) {
        predeliveryActions = (
          <PredeliveryActionsDropdown
            triggerCmp={
              <IconButton
                icon={
                  <UploadIcon
                    withHoverEffect
                    size={vars.iconSize.m}
                    fill={IconsFill.textBrand}
                  />
                }
              />
            }
            ticketGroup={{ tickets: posField(ticketGroup.tickets) }}
            onPredeliveryAction={(
              uploadPredeliveryAction: UploadPredeliveryAction
            ) => {
              onAmendTickets({
                additionalData: {
                  amendActionData: {
                    uploadPredeliveryAction: uploadPredeliveryAction,
                  },
                },
              });
            }}
          />
        );
      }

      if (
        original.isTktGrp ||
        (table.options.meta?.sectionType !== SectionType.PurchaseEvent &&
          table.options.meta?.sectionType !== SectionType.PurchaseSideTable)
      ) {
        return (
          <BaseCell align="right" showTooltip={false}>
            <Stack direction="row" gap="m" alignItems="center">
              {!linkOnly && ticketGroupNavId && (
                <>
                  <IconButton
                    icon={
                      <EditIcon
                        withHoverEffect
                        size={vars.iconSize.m}
                        fill={vars.color.textBrand}
                      />
                    }
                    onClick={() => onAmendTickets()}
                    titleContentId={ContentId.AmendTickets}
                  />
                  {ticketGroup?.canGetDocArts ? (
                    <LaunchDownloadUserDocuments
                      ticketGroupId={ticketGroup.tgId!}
                      iconOnlyMode
                    />
                  ) : (
                    predeliveryActions
                  )}
                </>
              )}
              <Link
                to={getPurchaseOrderRelativeUrl(
                  ticketGroupNavId ?? original.id
                )}
                onClick={(e) => {
                  if (
                    e.button === 0 &&
                    e.getModifierState('Control') === false
                  ) {
                    // Perform like a button click when it's a left-click (and no Ctrl)
                    // Else the click will be whatever <a /> does
                    e.preventDefault();
                    e.stopPropagation();

                    onViewTicketGroup(!!linkOnly);
                  }
                }}
              >
                <IconButton
                  icon={
                    <OpenLinkIcon
                      size={vars.iconSize.m}
                      fill={vars.color.textBrand}
                      withHoverEffect
                    />
                  }
                  titleContentId={ContentId.ViewDetails}
                />
              </Link>
            </Stack>
          </BaseCell>
        );
      }
      return (
        <BaseCell align="right" showTooltip={false}>
          <Stack direction="row" gap="m" alignItems="center">
            {original.ticketGroupItems?.length === 1 && (
              <>
                {ticketGroup?.canGetDocArts ? (
                  <LaunchDownloadUserDocuments
                    ticketGroupId={ticketGroup.tgId!}
                    iconOnlyMode
                  />
                ) : (
                  predeliveryActions
                )}
              </>
            )}
            <PurchaseActionsCell
              purchaseOrderId={original.id}
              linkOnly={linkOnly}
            />
          </Stack>
        </BaseCell>
      );
    },
  }),
  // VendorAccountEmail Column
  createCopyColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.VendorAccountEmail,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.VendorAccountEmail
      ],
    accessor: (data) =>
      data?.vendAcc?.email ? data.vendAcc.email.toLowerCase() : '',
    size: COLUMN_DEFAULT_SIZE_LG,
    shouldRender: (data, table) => {
      // We don't want to display this if the row is a ticket group
      const sectionType = table.options.meta?.sectionType;
      if (
        data?.isTktGrp &&
        sectionType !== SectionType.PurchaseEvent &&
        sectionType !== SectionType.PurchaseSideTable
      ) {
        return false;
      }
      return true;
    },
  }),
  // CreatedBy Column
  createUserTagColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.CreatedBy,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
        PurchasesTableColumnId.CreatedBy
      ],
    accessor: (data) => data?.insertBy,
    useUserDisplayHook: useApiFetchedUser,
    size: COLUMN_DEFAULT_SIZE_LG,
  }),
  // Event Column
  columnHelper.accessor((data) => data, {
    id: PurchasesTableColumnId.Event,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.Event
          ]
        }
      />
    ),
    enableColumnFilter: true,
    size: COLUMN_DEFAULT_SIZE_XL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, row: { original } }) {
      const multipleText = useContent(ContentId.Multiple);
      const purchaseOrder = getValue<PurchaseOrderTicketGroup>();
      const { data } = useCatalogDataContext();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      let displayText: string | null | undefined = null;
      let displayTitle: string | null | undefined = null;
      const { ticketGroupItems, isMultEv, isTktGrp, viagVirtId } =
        purchaseOrder;

      const isValidTicketGroups =
        ticketGroupItems != null && ticketGroupItems.length > 1;

      const getEventDisplayName = (event: Event | null | undefined) => {
        if (event?.evType === EVENT_TYPE_PARKING) {
          return event?.name;
        }
        return event?.shortName || event?.name;
      };

      const { event, performer } = getEventPerformerVenue(viagVirtId, data);
      const { event: firstTgEvent, performer: firstTgPerf } =
        getEventPerformerVenue(ticketGroupItems?.[0]?.viagVirtId, data);

      const eventDisplayName = getEventDisplayName(event?.event);
      const firstTgEventDisplayName = getEventDisplayName(firstTgEvent?.event);

      const performerDisplayName = performer?.shortName || performer?.name;
      const firstTgPerformerDisplayName =
        firstTgPerf?.shortName || firstTgPerf?.name;

      if (isTktGrp && event) {
        displayText = eventDisplayName;
        displayTitle = event.event.name;
      } else if (isValidTicketGroups) {
        const ticketGroupItemHasPerformer = !!firstTgPerf?.name;

        const performerName =
          performerDisplayName || firstTgPerformerDisplayName;
        const performerFullName = performer?.name || performer?.shortName;

        const eventName = eventDisplayName || firstTgEventDisplayName;
        const eventFullName = event?.event?.name || firstTgEvent?.event?.name;

        const displayName = ticketGroupItemHasPerformer
          ? performerName
          : eventName;
        const displayFullName = ticketGroupItemHasPerformer
          ? performerFullName
          : eventFullName;

        if (!isMultEv) {
          displayText = displayName
            ? `${displayName} (${multipleText})`
            : multipleText;
          displayTitle = displayFullName
            ? `${displayFullName} (${multipleText})`
            : multipleText;
        } else {
          const allEventsSameName = ticketGroupItemHasPerformer
            ? ticketGroupItems?.every((tg) => tg.perfId === firstTgPerf?.viagId)
            : ticketGroupItems?.every((tg) => {
                const tgEv = lookupEventInCatalog(data, tg.viagVirtId);
                return tgEv?.event?.name === firstTgEvent?.event?.name;
              });

          displayText =
            allEventsSameName && displayName
              ? `${displayName} (${multipleText})`
              : multipleText;
          displayTitle =
            allEventsSameName && displayFullName
              ? `${displayFullName} (${multipleText})`
              : multipleText;
        }
      } else if (event != null) {
        displayText = eventDisplayName;
        displayTitle = event?.event?.name;
      }

      return (
        <BaseCell title={displayTitle ?? undefined}>{displayText}</BaseCell>
      );
    },
    sortingFn: sortGeneric,
    filterFn: multiSelectFilter,
    meta: {
      columnFilterVariant: 'multiSelect',
    },
  }),
  // Venue Column
  columnHelper.accessor((data) => data, {
    id: PurchasesTableColumnId.Venue,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.Venue
          ]
        }
      />
    ),
    enableColumnFilter: true,
    size: COLUMN_DEFAULT_SIZE_XL,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, row: { original } }) {
      const purchaseOrder = getValue<PurchaseOrderTicketGroup>();
      const multipleText = useContent(ContentId.Multiple);
      const { data } = useCatalogDataContext();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      const { ticketGroupItems, isMultEv, tktGrpCnt, isTktGrp, viagVirtId } =
        purchaseOrder;

      const { venue } = getEventPerformerVenue(viagVirtId, data);

      const isMultipleEvents = isMultEv || tktGrpCnt > 1;

      const isValidTicketGroups =
        ticketGroupItems != null && ticketGroupItems.length > 1;

      let displayText: string | null = null;
      let displayTitle: string | null = null;

      if (isTktGrp && venue != null) {
        displayText = venue.shortName || venue.name;
        displayTitle = venue.name;
      } else if (isValidTicketGroups) {
        const { venue: firstTgVenue } = getEventPerformerVenue(
          ticketGroupItems?.[0]?.viagVirtId,
          data
        );

        const venueName =
          venue?.shortName ||
          venue?.name ||
          firstTgVenue?.shortName ||
          firstTgVenue?.name;
        const venueFullName = venue?.name || firstTgVenue?.name;

        if (!isMultipleEvents) {
          displayText = venueName ? venueName : multipleText;
          displayTitle = venueFullName ? venueFullName : multipleText;
        } else {
          const allEventsSameVenue = ticketGroupItems?.every(
            (tg) => tg.venId === firstTgVenue?.viagId
          );

          displayText =
            allEventsSameVenue && venueName ? venueName : multipleText;
          displayTitle =
            allEventsSameVenue && venueFullName ? venueFullName : multipleText;
        }
      } else if (venue != null) {
        displayText = venue.shortName || venue.name;
        displayTitle = venue.name;
      }

      if (displayText == null) {
        return null;
      }

      return (
        <TextCell
          value={displayText}
          style={{ color: vars.color.textLocationV2 }}
        />
      );
    },
    filterFn: multiSelectFilter,
    meta: {
      columnFilterVariant: 'multiSelect',
    },
    sortingFn: sortGeneric,
  }),
  // EventDate Column
  columnHelper.accessor((data) => data, {
    id: PurchasesTableColumnId.EventDate,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.EventDate
          ]
        }
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, row: { original }, table }) {
      const purchaseOrder = getValue<PurchaseOrderTicketGroup>();
      const multipleText = useContent(ContentId.Multiple);
      const { data } = useCatalogDataContext();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      if (!purchaseOrder.isTktGrp && purchaseOrder.isMultEv) {
        return <BaseCell>{multipleText}</BaseCell>;
      }

      const event = lookupEventInCatalog(data, purchaseOrder.viagVirtId);
      if (event?.event == null) {
        return null;
      }

      const dates = event.event.dates;

      const customPrecision =
        table.options.meta?.formatOptionOverrides?.[
          PurchasesTableColumnId.EventDate
        ];

      const date = customPrecision
        ? (applyNumberFormatting({
            precision: customPrecision,
            inputNumber: dates?.start,
            isDateTime: true,
          }) as string)
        : undefined;

      return (
        <BaseCell>
          {date ? (
            <div
              style={{
                textTransform: 'none',
              }}
            >
              {date}
            </div>
          ) : (
            <EventDateDisplay
              eventDateString={dates?.start ?? undefined}
              eventEndDateString={dates?.end ?? undefined}
              showEventDateTbd={true}
              eventSubDateFormat={' - h:mm aaa, eee'}
              slimMode
              displayClassName={styles.eventDateCell}
            />
          )}
        </BaseCell>
      );
    },
    sortingFn: 'datetime',
  }),
  // Seating Column
  createSeatingColumn<PurchaseOrderTicketGroup | null>({
    id: PurchasesTableColumnId.Seating,
    headerContentId:
      PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[PurchasesTableColumnId.Seating],
    accessor: (data) => {
      if (!data) {
        return null;
      }

      // Handle multiple ticket groups or multi-event orders
      if (!data.isTktGrp && (data.isMultEv || data.tktGrpCnt > 1)) {
        return {
          seating: {
            section: '',
            sectionId: null,
            row: null,
            rowId: null,
            seatFr: null,
            seatTo: null,
            ticketIds: null,
            listingIds: null,
          },
          listingNotes: null,
          isSeatSaver: false,
          isMultiple: true,
        };
      }

      const ticketGroupToGetSeating = data.isTktGrp
        ? data
        : data.ticketGroupItems?.[0];

      if (ticketGroupToGetSeating?.seating == null) {
        return null;
      }

      return {
        seating: ticketGroupToGetSeating.seating,
        listingNotes: ticketGroupToGetSeating.seatTraits,
        isSeatSaver: false,
        isMultiple: false,
      };
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    sectionType: SectionType.PurchaseEvent,
    sortingFn: SEATING_SORTING_FN_NAME,
    enableSorting: isSearchV2 ? false : undefined,
  }),
  // Row Column
  columnHelper.accessor((data) => getSeating(data)?.row ?? '', {
    id: PurchasesTableColumnId.Row,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[PurchasesTableColumnId.Row]
        }
      />
    ),
    enableColumnFilter: true,
    meta: {
      columnFilterVariant: 'multiSelect',
    },
    filterFn: multiSelectFilter,
    size: COLUMN_DEFAULT_SIZE_BASE,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    ...(isSearchV2 && { enableSorting: false }),
    cell: function Cell({ getValue, row: { original: purchaseOrder } }) {
      const multipleText = useContent(ContentId.Multiple);
      if (!purchaseOrder) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      if (
        !purchaseOrder.isTktGrp &&
        (purchaseOrder.isMultEv || purchaseOrder.tktGrpCnt > 1)
      ) {
        return <BaseCell>{multipleText}</BaseCell>;
      }

      const ticketGroupToGetSeating = purchaseOrder.isTktGrp
        ? purchaseOrder
        : purchaseOrder.ticketGroupItems?.[0];

      if (ticketGroupToGetSeating?.seating == null) {
        return null;
      }

      const { seating } = ticketGroupToGetSeating;
      return <BaseCell align="right">{seating.row}</BaseCell>;
    },
    sortingFn: sortGeneric,
  }),
  // Seats Column
  columnHelper.accessor((data) => getSeating(data), {
    id: PurchasesTableColumnId.Seats,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.Seats
          ]
        }
      />
    ),
    enableColumnFilter: true,
    filterFn:
      seatRangeFilter as FilterFnOption<PurchaseOrderTicketGroup | null>,
    meta: {
      columnFilterVariant: 'numberRange',
    },
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    ...(isSearchV2 && { enableSorting: false }),
    cell: function Cell({ row: { original: purchaseOrder } }) {
      const multipleText = useContent(ContentId.Multiple);
      if (!purchaseOrder) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      if (
        !purchaseOrder.isTktGrp &&
        (purchaseOrder.isMultEv || purchaseOrder.tktGrpCnt > 1)
      ) {
        return <BaseCell>{multipleText}</BaseCell>;
      }

      const ticketGroupToGetSeating = purchaseOrder.isTktGrp
        ? purchaseOrder
        : purchaseOrder.ticketGroupItems?.[0];

      if (ticketGroupToGetSeating?.seating == null) {
        return null;
      }

      const { seating } = ticketGroupToGetSeating;
      return (
        <BaseCell align="right">
          {getSeatDisplayText({
            seatFr: seating.seatFr,
            seatTo: seating.seatTo,
          })}
        </BaseCell>
      );
    },
    sortingFn: sortSeatingSeatsForTable,
  }),
  // SeatTraits Column
  columnHelper.accessor(
    (data) => {
      const ticketGroupToGetSeating = data?.isTktGrp
        ? data
        : data?.ticketGroupItems?.[0];

      return ticketGroupToGetSeating?.seatTraits;
    },
    {
      id: PurchasesTableColumnId.SeatTraits,
      header: () => (
        <Content
          id={
            PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
              PurchasesTableColumnId.SeatTraits
            ]
          }
        />
      ),
      enableColumnFilter: true,
      filterFn:
        seatTraitsFilter as FilterFnOption<PurchaseOrderTicketGroup | null>,
      meta: {
        columnFilterVariant: 'multiSelect',
        uniqueValueVariant: 'seatTraits',
      },
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original: purchaseOrder } }) {
        const multipleText = useContent(ContentId.Multiple);
        if (!purchaseOrder) {
          return (
            <TableShimmeringDiv
              height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE}
            />
          );
        }

        if (
          !purchaseOrder.isTktGrp &&
          (purchaseOrder.isMultEv || purchaseOrder.tktGrpCnt > 1)
        ) {
          return <BaseCell>{multipleText}</BaseCell>;
        }

        const listingNotes = getValue<ListingNote[] | null | undefined>();

        return (
          <BaseCell showTooltip={false}>
            {listingNotes?.length ? (
              <ListingNotesDisplay seatTraits={listingNotes} />
            ) : null}
          </BaseCell>
        );
      },
      enableSorting: false,
    }
  ),
  // Commissions Column
  columnHelper.accessor((data) => data?.commissions, {
    id: PurchasesTableColumnId.Commissions,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.Commissions
          ]
        }
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, row: { original: purchaseOrder } }) {
      if (!purchaseOrder) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      const commissions = getValue<Commission[] | null>();

      if (!commissions) {
        return null;
      }

      return (
        <BaseCell align="right">
          <CommissionsCell commissions={commissions} />
        </BaseCell>
      );
    },
    sortingFn: sortGeneric,
  }),
  // ConsigneeSellerAccountId Column
  columnHelper.accessor((data) => data?.accId || null, {
    id: PurchasesTableColumnId.ConsigneeSellerAccountId,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.ConsigneeSellerAccountId
          ]
        }
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    ...(isSearchV2 && { enableSorting: false }),
    cell: function Cell({ getValue, row: { original }, table }) {
      const { accountName } =
        useConsigneeAccountDisplayName(getValue<string | null>());

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      return <BaseCell>{accountName}</BaseCell>;
    },
  }),

  // OfficeId Column
  columnHelper.accessor((data) => data?.officeId ?? null, {
    id: PurchasesTableColumnId.Office,
    header: () => (
      <Content
        id={
          PURCHASE_TABLE_V2_COLUMN_ID_TO_CONTENT_ID[
            PurchasesTableColumnId.Office
          ]
        }
      />
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    ...(isSearchV2 && { enableSorting: false }),
    cell: function Cell({ getValue, row: { original } }) {
      const { getOfficeName } = useOffices();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_PURCHASE} />
        );
      }

      const officeId = getValue<number | null>();
      const officeName = officeId != null ? getOfficeName(officeId) : null;

      return <BaseCell>{officeName}</BaseCell>;
    },
  }),
];
